import { backOff } from 'exponential-backoff';
import type { GetCampaignResponse } from '@ab-inbev-eu/campaign/src/models/campaign';

import type { Card } from '../app/[lng]/qr/[qrId]/[...route]/_components/card-collection/CardCollectionWrapper';

import dayjs from './dayjs';

export async function retry<T>(fn: () => Promise<T>): Promise<T> {
  return backOff(fn, {
    numOfAttempts: 5,
    startingDelay: 250,
    maxDelay: 2000,
  });
}

export function getCampaignStatus(campaign: Pick<GetCampaignResponse, 'startDate' | 'endDate'>) {
  const now = dayjs();

  if (campaign.startDate && dayjs(campaign.startDate).isAfter(now)) {
    return 'notStarted';
  }
  if (campaign.endDate && dayjs(campaign.endDate).isBefore(now)) {
    return 'ended';
  }

  return 'live';
}

export function maskString(input: string): string {
  if (input.length <= 6) {
    return '****';
  }
  const visiblePartLength = Math.min(4, input.length);
  const maskedPart = '****';
  const visibleEndPart = input.slice(-4);
  return `${input.slice(0, visiblePartLength)}${maskedPart}${visibleEndPart}`;
}

export function getTranslationByLanguage(
  language: string,
  collectionName?: Record<string, string>,
): string | undefined {
  if (!collectionName) return undefined;
  if (!collectionName[language]) return collectionName['en'] || undefined;
  return collectionName[language];
}

export function extractLanguageAtIso2(lng: string): string {
  return lng.split('-')[0];
}

export function snakeToTitleCase(str: string) {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getLadderCards(count: number, cards: Card[]): Card[] {
  const copyOfCards = cards.toSorted((a, b) => (a.scansLeft ?? 0) - (b.scansLeft ?? 0));
  const firstInactiveCardIndex = copyOfCards.findIndex((card) => !card.active);
  if (firstInactiveCardIndex === -1) return copyOfCards;
  const cardsWithCount = copyOfCards.map((card, index) => {
    if (index < firstInactiveCardIndex) return card;
    if (index === firstInactiveCardIndex) {
      return {
        ...card,
        scansLeft: card.scansLeft ? card.scansLeft - count : undefined,
      };
    }
    return {
      ...card,
      scansLeft: undefined,
    };
  });
  return cardsWithCount;
}
